import { INotifications } from '@app/@introspection';
import { Inject, Injectable, Injector } from '@rxdi/core';
import { ApolloClient, deepCopy } from '@rxdi/graphql-client';
import { SocialService } from '@stradivario/graphql-server';
import { BehaviorSubject, from, Subscription } from 'rxjs';
import { delay, filter, map, switchMap, tap } from 'rxjs/operators';
import { info } from 'toastr';

import { BaseGraphqlService } from '../base';

@Injectable()
export class NotificationsService extends BaseGraphqlService {
  @Injector(ApolloClient)
  public graphql: ApolloClient;

  private readonly list: BehaviorSubject<INotifications[]> = new BehaviorSubject([]);

  private count: number;
  private invitesCount: number;

  private subscription: Subscription;

  @Inject(SocialService)
  private socialService: SocialService;

  OnInit() {
    this.subscription = this.socialService
      .checkLoggedIn()
      .pipe(
        filter((i) => !!i),
        delay(500),
        switchMap(() => this.getNotifications()),
        tap((notifications) => {
          this.count = notifications.count;
          this.list.next(deepCopy(notifications.data));
        }),
        switchMap(() =>
          this.listenForNotifications().pipe(
            tap((res) => {
              info('Notification received', 'Project', {
                positionClass: 'toast-bottom-left',
              });
              this.list.next([...this.list.getValue(), res.data.notifications]);
            })
          )
        )
      )
      .subscribe();
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  subscribeToNotifications() {
    return this.list.asObservable();
  }

  listenForNotifications() {
    return from(
      this.subscribe({
        query: 'listen-for-notifications.graphql',
      })
    );
  }

  getPagesCount() {
    return this.count;
  }

  getInvitesCount() {
    return this.invitesCount;
  }

  getNotifications(limit = 1000, offset = 0) {
    return from(
      this.query({
        query: 'get-notifications.type.graphql',
        variables: {
          sort: 'ASC',
          limit,
          offset,
        },
      })
    ).pipe(map((res) => res.data.getNotifications));
  }

  readNotification(id: string) {
    return from(
      this.mutate({
        mutation: 'read-notification.graphql',
        variables: {
          id,
        },
      })
    ).pipe(
      map(({ data }) => data.readNotification),
      tap(() => {
        const notifications = this.list.getValue();
        const notification = notifications.find((n) => n.id === id);
        notification.read = true;
        this.list.next(notifications);
      })
    );
  }
}
